const formatMoney = (value) => `$${Number(value || 0).toLocaleString('de-DE')}`;

const getUnitPrice = (product, cartItem) => {
  if (cartItem.wholesale?.bool && product.product_wholesaler?.length) {
    const wholesale = product.product_wholesaler.find((p) => p.id === cartItem.wholesale.id);
    if (wholesale) return wholesale.price;
  }

  let unitPrice = product.sale || product.price;
  if (cartItem.descriptionId != null) {
    const description = product.stock_product_descriptions?.find(
      (stockDescription) => stockDescription.id === cartItem.descriptionId,
    );
    if (description?.price > 0) unitPrice = description.price;
  }
  return unitPrice;
};

const COLOR_CATEGORY_ID = 1;
const SIZE_CATEGORY_ID = 2;

const getSpecificationParts = (product, cartItem) => {
  if (!cartItem.descriptionId || !product.stock_product_descriptions?.length) return [];

  const description = product.stock_product_descriptions.find(
    (stockDescription) => stockDescription.id === cartItem.descriptionId,
  );
  if (!description?.stock_descriptions) return [];

  const specs = product.stock_product_descriptions;
  const categoryId = description.stock_descriptions.stock_category_id;
  const combination = description.combination;

  const findSpecByCategory = (targetCategoryId) => specs.find(
    (stockDescription) => stockDescription.stock_descriptions?.stock_category_id === targetCategoryId
      && (combination
        ? stockDescription.combination === combination
        : stockDescription.id === cartItem.descriptionId),
  );

  const colorDescription = categoryId === COLOR_CATEGORY_ID
    ? description
    : findSpecByCategory(COLOR_CATEGORY_ID);

  const sizeDescription = categoryId === SIZE_CATEGORY_ID
    ? description
    : findSpecByCategory(SIZE_CATEGORY_ID);

  const parts = [];
  const colorName = colorDescription?.stock_descriptions?.name;
  const sizeName = sizeDescription?.stock_descriptions?.name;

  if (colorName) parts.push(`Color: ${colorName}`);
  if (sizeName) parts.push(`Talle: ${sizeName}`);

  if (!parts.length && description.stock_descriptions.name) {
    parts.push(description.stock_descriptions.name);
  }

  return parts;
};

const getVariantLabel = (product, cartItem) => getSpecificationParts(product, cartItem).join(', ');

export const buildCheckoutSummary = ({
  items = [],
  products = [],
  formData = {},
  checkoutType = 'delivery',
  subtotal = 0,
  shippingCost = 0,
  coupon = null,
  clientName = '',
  paymentMethod = '',
}) => {
  const lines = items.map((cartItem) => {
    const product = products.find((p) => p.id === cartItem.itemId);
    if (!product) return null;

    const unitPrice = getUnitPrice(product, cartItem);
    const lineTotal = unitPrice * cartItem.quantity;
    const variant = getVariantLabel(product, cartItem);
    const wholesaleLabel = cartItem.wholesale?.quantity
      ? `Pack x${cartItem.wholesale.quantity}`
      : null;

    return {
      name: product.name,
      quantity: cartItem.quantity,
      unitPrice,
      lineTotal,
      variant,
      specificationParts: getSpecificationParts(product, cartItem),
      wholesaleLabel,
    };
  }).filter(Boolean);

  const couponDiscount = coupon?.percentage
    ? (coupon.percentage / 100) * subtotal
    : (coupon?.amount || 0);

  const productsTotal = Math.max(0, subtotal - couponDiscount);
  const total = formData.transaction_amount ?? (productsTotal + (shippingCost || 0));

  return {
    clientName,
    checkoutType,
    formData,
    lines,
    subtotal,
    shippingCost: shippingCost || 0,
    couponDiscount,
    total,
    paymentMethod,
  };
};

export const buildWhatsAppOrderMessage = (summary, orderId) => {
  if (!summary) {
    return `Hola. Acabo de realizar el pedido #${orderId}. Quiero coordinar el pago y el envío. ¡Gracias!`;
  }

  const storeName = summary.clientName || 'la tienda';
  const productLines = summary.lines.map((line, index) => {
    const parts = [
      `${index + 1}. ${line.name} x${line.quantity} - ${formatMoney(line.unitPrice)} c/u = ${formatMoney(line.lineTotal)}`,
    ];
    if (line.specificationParts?.length) {
      parts.push(`   Especificación: ${line.specificationParts.join(', ')}`);
    } else if (line.variant) {
      parts.push(`   Especificación: ${line.variant}`);
    }
    if (line.wholesaleLabel) parts.push(`   ${line.wholesaleLabel}`);
    return parts.join('\n');
  }).join('\n');

  const isPickup = summary.checkoutType === 'pickup';
  const formData = summary.formData || {};
  const contactBlock = isPickup
    ? [
      `Nombre: ${formData.first_name_retire || ''} ${formData.last_name_retire || ''}`.trim(),
      `Teléfono: ${formData.phone_retire || '-'}`,
      `Email: ${formData.email_retire || '-'}`,
      formData.id_card_retire ? `DNI: ${formData.id_card_retire}` : null,
    ]
    : [
      `Nombre: ${formData.first_name || ''} ${formData.last_name || ''}`.trim(),
      `Teléfono: ${formData.phone || '-'}`,
      `Email: ${formData.email || '-'}`,
      `Dirección: ${[formData.address, formData.number].filter(Boolean).join(' ')}`.trim(),
      formData.locality ? `Localidad: ${formData.locality}` : null,
      formData.postal_code ? `CP: ${formData.postal_code}` : null,
    ];

  return [
    `Hola, ${storeName}. Acabo de realizar un pedido.`,
    '',
    `*Pedido #${orderId}*`,
    '',
    '*Productos:*',
    productLines || 'Sin detalle de productos',
    '',
    `Subtotal: ${formatMoney(summary.subtotal)}`,
    summary.couponDiscount > 0 ? `Descuento cupón: -${formatMoney(summary.couponDiscount)}` : null,
    summary.shippingCost > 0 ? `Envío: ${formatMoney(summary.shippingCost)}` : null,
    `*Total: ${formatMoney(summary.total)}*`,
    '',
    `*Entrega:* ${isPickup ? 'Retiro en sucursal' : 'Envío a domicilio'}`,
    summary.paymentMethod ? `*Forma de pago:* ${summary.paymentMethod}` : null,
    formData.comments ? `*Comentarios:* ${formData.comments}` : null,
    '',
    '*Mis datos:*',
    ...contactBlock.filter(Boolean),
    '',
    'Quiero coordinar el pago y el envío. ¡Gracias!',
  ].filter(Boolean).join('\n');
};

export const normalizeWhatsappNumber = (raw) => {
  if (!raw) return '';
  let digits = String(raw).replace(/\D/g, '');
  if (digits.startsWith('0')) {
    digits = `54${digits.slice(1)}`;
  }
  return digits;
};

export const isValidWhatsAppNumber = (number) => {
  const normalized = normalizeWhatsappNumber(number);
  return /^[1-9]\d{7,14}$/.test(normalized);
};

export const resolveWhatsappNumber = ({ whatsapp, client } = {}) => {
  const candidates = [whatsapp?.number, client?.phone, client?.whatsapp];
  const found = candidates
    .map((value) => normalizeWhatsappNumber(value))
    .find((value) => isValidWhatsAppNumber(value));
  return found || '';
};

export const openWhatsAppChat = (whatsappNumber, message) => {
  const normalizedNumber = normalizeWhatsappNumber(whatsappNumber);
  if (!isValidWhatsAppNumber(normalizedNumber)) return;

  const url = `https://wa.me/${normalizedNumber}?text=${encodeURIComponent(message)}`;
  window.open(url, '_blank', 'noopener,noreferrer');
};
